import { useEffect, useState } from 'react';
import {
  XMarkIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  MapPinIcon,
  VideoCameraIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { CalendlyEventDetails } from '../types/checkout';
import { SchedulingWidget } from './SchedulingWidget';

interface Props {
  details: CalendlyEventDetails | null;
  loading: boolean;
  error: boolean;
  onClose: () => void;
  /** Called when the user exits the reschedule view — triggers a re-fetch of event details. */
  onRescheduleViewClosed: () => void;
}

type View = 'details' | 'rescheduling';

function formatTimeRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const dateStr = start.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const startTime = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  const endTime = end.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `${dateStr} · ${startTime} – ${endTime}`;
}

const videoConferenceTypes = new Set([
  'zoom',
  'google_conference',
  'microsoft_teams_conference',
  'webex_conference',
  'gotomeeting',
]);

function LocationRow({
  location,
}: {
  location: CalendlyEventDetails['location'];
}) {
  if (!location) return null;

  const isVideo = videoConferenceTypes.has(location.type);
  const isPhone =
    location.type === 'outbound_call' || location.type === 'inbound_call';

  if (isVideo && location.joinUrl) {
    return (
      <div className="flex items-start gap-3">
        <VideoCameraIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--dash-text-faint)]" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--dash-text-faint)]">
            Meeting Link
          </p>
          <a
            href={location.joinUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 inline-flex items-center gap-1 text-sm text-[var(--dash-gold)] hover:text-[var(--dash-gold-bright)]"
          >
            Join Meeting
            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  }

  if (isPhone && location.location) {
    return (
      <div className="flex items-start gap-3">
        <PhoneIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--dash-text-faint)]" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--dash-text-faint)]">
            Phone
          </p>
          <p className="mt-0.5 text-sm text-[var(--dash-text)]">
            {location.location}
          </p>
          {location.additionalInfo && (
            <p className="mt-0.5 text-xs text-[var(--dash-text-muted)]">
              {location.additionalInfo}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (location.location) {
    return (
      <div className="flex items-start gap-3">
        <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--dash-text-faint)]" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--dash-text-faint)]">
            Location
          </p>
          <p className="mt-0.5 text-sm text-[var(--dash-text)]">
            {location.location}
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default function AppointmentDetailsModal({
  details,
  loading,
  error,
  onClose,
  onRescheduleViewClosed,
}: Props) {
  const [view, setView] = useState<View>('details');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (view === 'rescheduling') {
          setView('details');
          onRescheduleViewClosed();
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, onRescheduleViewClosed, view]);

  // Reset to details view whenever the modal opens with fresh data.
  useEffect(() => {
    if (!loading && details) {
      setView('details');
    }
  }, [loading, details]);

  const handleRescheduleBack = () => {
    setView('details');
    onRescheduleViewClosed();
  };

  const isCancelled =
    details?.status === 'canceled' || details?.status === 'cancelled';

  const isRescheduling = view === 'rescheduling';

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={isRescheduling ? undefined : onClose}
    >
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`w-full rounded-[10px] border border-[var(--dash-border-strong)] bg-[var(--dash-card-light)] shadow-xl transition-all ${
            isRescheduling ? 'max-w-2xl' : 'max-w-md'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--dash-border)] px-6 py-4">
            <h2 className="font-serif text-lg text-[var(--dash-text)]">
              {isRescheduling
                ? 'Reschedule Appointment'
                : 'Appointment Details'}
            </h2>
            <button
              onClick={view === 'rescheduling' ? handleRescheduleBack : onClose}
              className="flex h-7 w-7 items-center justify-center rounded text-[var(--dash-text-faint)] transition-colors hover:bg-[var(--dash-hover-bg)] hover:text-[var(--dash-text)]"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            {isRescheduling && details?.rescheduleUrl ? (
              <div className="space-y-4">
                <SchedulingWidget
                  embedUrl={details.rescheduleUrl}
                  onEventScheduled={handleRescheduleBack}
                  title="Reschedule appointment"
                />
                <div className="flex justify-center">
                  <button
                    onClick={handleRescheduleBack}
                    className="flex items-center gap-1.5 text-sm text-[var(--dash-text-faint)] transition-colors hover:text-[var(--dash-text)]"
                  >
                    <ArrowLeftIcon className="h-3.5 w-3.5" />
                    Back
                  </button>
                </div>
              </div>
            ) : (
              <>
                {loading && (
                  <p className="text-sm text-[var(--dash-text-faint)]">
                    Loading…
                  </p>
                )}

                {!loading && error && (
                  <p className="text-sm text-[var(--dash-text-faint)]">
                    Could not load appointment details. Please try again later.
                  </p>
                )}

                {!loading && !error && details && (
                  <div className="space-y-5">
                    {/* Event name + status */}
                    <div>
                      <h3 className="font-serif text-[17px] text-[var(--dash-text)]">
                        {details.name}
                      </h3>
                      <div className="mt-1.5">
                        {isCancelled ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--dash-border)] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-[var(--dash-text-muted)]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[var(--dash-text-muted)]" />
                            Cancelled
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--dash-border)] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-[var(--dash-status-green)]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[var(--dash-status-green)]" />
                            Active
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Date & time */}
                    <div className="flex items-start gap-3">
                      <CalendarDaysIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--dash-text-faint)]" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--dash-text-faint)]">
                          Date & Time
                        </p>
                        <p className="mt-0.5 text-sm text-[var(--dash-text)]">
                          {formatTimeRange(details.startTime, details.endTime)}
                        </p>
                      </div>
                    </div>

                    {/* Location */}
                    <LocationRow location={details.location} />

                    {/* Actions */}
                    {!isCancelled && (
                      <div className="space-y-3 border-t border-[var(--dash-border)] pt-4">
                        {details.rescheduleUrl && (
                          <div className="flex justify-center">
                            <button
                              onClick={() => setView('rescheduling')}
                              className="inline-flex items-center gap-1.5 rounded border border-[var(--dash-gold-border)] px-5 py-2 text-sm font-semibold uppercase tracking-widest text-[var(--dash-gold)] transition-colors hover:border-[var(--dash-gold)] hover:text-[var(--dash-gold-bright)]"
                            >
                              Reschedule
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-[var(--dash-text-faint)]">
                          Need to cancel? Contact our support team and we'll
                          take care of it for you.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
